export type MyGigsTab = 'inquiries' | 'applicants' | 'offers' | 'payment_history';

export type InquiryStatus = 'new' | 'replied' | 'offer_sent' | 'closed';

export interface InquiryMessage {
  id: string;
  senderName: string;
  senderAvatar?: string;
  isUser: boolean;
  text: string;
  timestamp: string;
}

export interface InquiryActivity {
  label: string;
  timestamp: string;
  status: 'received' | 'viewed' | 'offer_sent' | 'accepted' | 'rejected';
}

export interface InquiryItem {
  id: string;
  guestName: string;
  guestEmail: string;
  avatarColor?: string;
  status: InquiryStatus;
  interestedIn: string;
  source: string;
  message: string;
  timestamp: string;
  receivedAt: string;
  applicantId?: number;
  applicantAvatar?: string;
  serviceId?: number;
  attachments?: { name: string; url: string; file_size?: number }[];
  activity?: InquiryActivity[];
  conversation?: InquiryMessage[];
}

export type GigApplicationStatus = 'pending' | 'accepted' | 'rejected';

export interface SocialLinks {
  spotify?: string;
  appleMusic?: string;
  tiktok?: string;
  website?: string;
  instagram?: string;
  twitter?: string;
}

export interface ApplicantItem {
  application_uuid: string;
  status: GigApplicationStatus;
  fullName: string;
  username: string;
  email: string;
  profileImage: string | null;
  userTypes: string[];
  location: string;
  timeAgo: string;
  appliedToGig: string;
  gigDescription?: string;
  gigUuid?: string | null;
  applicantUserId?: number | null;
  bio: string;
  genres: string[];
  skills: string[];
  socialLinks: SocialLinks;
}

/** API-backed offer lifecycle statuses */
export type OfferStatus =
  | 'draft'
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'withdrawn'
  | 'expired'
  | 'changes_requested';

export type OfferPaymentStatus = 'unpaid' | 'partially_paid' | 'paid' | 'failed';

export type CancellationPolicy = 'flexible' | 'moderate' | 'strict';

/** Presentation-only badge labels derived from status + payment_status */
export type OfferDisplayStatus =
  | 'draft'
  | 'pending'
  | 'awaiting_funding'
  | 'accepted'
  | 'in_progress'
  | 'completed'
  | 'rejected'
  | 'withdrawn'
  | 'expired'
  | 'changes_requested';

export interface OfferMilestone {
  id: string;
  title: string;
  amount: number;
  percentage?: number;
  dueDate: string;
  status: 'paid' | 'unpaid';
  paymentStatus?: OfferPaymentStatus;
  position?: number;
}

export interface OfferPayment {
  id: number;
  transactionId?: string;
  amount: number;
  platformFeeAmount?: number;
  processingFeeAmount?: number;
  payerTotalAmount?: number;
  providerPayoutAmount?: number;
  status: string;
  /** Lowercased `payment_channel`; anything other than `stripe` is an off-platform settlement. */
  channel?: string;
  note?: string;
  /** User who recorded an off-platform settlement, when the API exposes it. */
  proposedById?: number | null;
  milestoneId?: number | null;
  milestoneIds?: number[];
  stripePaymentLinkUrl?: string | null;
  createdAt?: string;
}

export type OfferPaymentState = 'pending' | 'confirmed' | 'rejected';

export function isOffPlatformPayment(payment: Pick<OfferPayment, 'channel'>): boolean {
  return !!payment.channel && payment.channel !== 'stripe';
}

export function getOfferPaymentState(payment: Pick<OfferPayment, 'status'>): OfferPaymentState {
  const s = payment.status.toLowerCase();
  if (['paid', 'confirmed', 'succeeded', 'completed', 'complete', 'settled'].includes(s)) {
    return 'confirmed';
  }
  if (['rejected', 'failed', 'cancelled', 'canceled', 'expired', 'declined'].includes(s)) {
    return 'rejected';
  }
  return 'pending';
}

export interface OfferChangeRequest {
  id: number;
  authorId?: number;
  requestedChanges?: string;
  proposedTerms?: string;
  createdAt?: string;
}

export interface OfferItem {
  id: string;
  inquiryId?: string;
  inquiryTitle?: string;
  recipientName: string;
  recipientEmail: string;
  recipientUserId?: number | null;
  recipientUsername?: string;
  recipientAvatar?: string;
  recipientRole?: string;
  recipientTags?: string[];
  avatarInitial?: string;
  avatarColor?: string;
  verified?: boolean;
  creatorId: number;
  creatorName?: string;
  creatorAvatar?: string;
  creatorRole?: string;
  gigUuid?: string | null;
  conversationId?: string | null;
  predecessorOfferUuid?: string | null;
  commercialMode?: string | null;
  cancellationPolicy?: CancellationPolicy | null;
  declineReason?: string;
  /** True when the candidate can receive payouts, false when they cannot, null when the API does not say. */
  recipientPayoutReady?: boolean | null;
  title: string;
  description: string;
  deliverables: string | string[];
  currency: string;
  price: number;
  deposit?: number;
  hasPaymentSchedule?: boolean;
  paymentType?: 'milestone' | 'single';
  milestones: OfferMilestone[];
  payments?: OfferPayment[];
  startDate: string;
  endDate?: string;
  deliveryDate?: string;
  revisionsIncluded: number;
  expirationDate?: string;
  timezone?: string;
  expiresInText?: string;
  attachments?: { name: string; url: string; file_size?: number }[];
  internalNotes?: string;
  status: OfferStatus;
  paymentStatus: OfferPaymentStatus;
  amountPaid?: number;
  remainingBalance?: number;
  stripePaymentLinkUrl?: string | null;
  sentAt?: string;
  acceptedAt?: string;
  createdAt?: string;
  feedback?: string;
  changeRequests?: OfferChangeRequest[];
}

export interface OfferPaymentProgress {
  amountPaid?: number;
  remainingBalance?: number;
  milestones?: Pick<OfferMilestone, 'status'>[];
}

/**
 * Resolves the effective payment status, falling back to amounts and milestone
 * state when the backend's offer-level payment_status is missing or stale.
 */
export function resolveOfferPaymentStatus(
  paymentStatus: OfferPaymentStatus = 'unpaid',
  progress?: OfferPaymentProgress,
): OfferPaymentStatus {
  if (paymentStatus === 'paid' || paymentStatus === 'partially_paid') return paymentStatus;
  if (!progress) return paymentStatus;

  const milestones = progress.milestones ?? [];
  const paidMilestones = milestones.filter((m) => m.status === 'paid').length;
  const amountPaid = progress.amountPaid ?? 0;
  const hasPayment = amountPaid > 0 || paidMilestones > 0;
  if (!hasPayment) return paymentStatus;

  const allMilestonesPaid = milestones.length > 0 && paidMilestones === milestones.length;
  const remaining = progress.remainingBalance;
  if (allMilestonesPaid || (remaining != null && remaining <= 0.001)) return 'paid';
  return 'partially_paid';
}

type OfferExpiryFields = Pick<OfferItem, 'status' | 'expirationDate'>;

/** Epoch ms of the offer's expiration instant, or null when none is set. */
export function getOfferExpiryMs(offer: Pick<OfferItem, 'expirationDate'>): number | null {
  if (!offer.expirationDate) return null;
  const ms = Date.parse(offer.expirationDate);
  return Number.isFinite(ms) ? ms : null;
}

export function isOfferPastExpiry(
  offer: Pick<OfferItem, 'expirationDate'>,
  nowMs: number = Date.now(),
): boolean {
  const expiry = getOfferExpiryMs(offer);
  return expiry != null && expiry <= nowMs;
}

/**
 * The backend flips `pending` → `expired` asynchronously, so a pending offer
 * whose expiration has passed is already expired for every UI decision.
 */
export function isOfferEffectivelyExpired(
  offer: OfferExpiryFields,
  nowMs: number = Date.now(),
): boolean {
  if (offer.status === 'expired') return true;
  return offer.status === 'pending' && isOfferPastExpiry(offer, nowMs);
}

export function getEffectiveOfferStatus(
  offer: OfferExpiryFields,
  nowMs: number = Date.now(),
): OfferStatus {
  return isOfferEffectivelyExpired(offer, nowMs) ? 'expired' : offer.status;
}

export function withEffectiveOfferStatus<T extends OfferExpiryFields>(
  offer: T,
  nowMs: number = Date.now(),
): T {
  const status = getEffectiveOfferStatus(offer, nowMs);
  return status === offer.status ? offer : { ...offer, status };
}

export function getOfferDisplayStatus(
  status: OfferStatus,
  paymentStatus: OfferPaymentStatus = 'unpaid',
  isRecipient = false,
  progress?: OfferPaymentProgress,
): OfferDisplayStatus {
  if (status === 'accepted') {
    const effective = resolveOfferPaymentStatus(paymentStatus, progress);
    if (effective === 'paid') return 'completed';
    if (effective === 'partially_paid') return 'in_progress';
    // Recipient sees confirmation; creator must still fund escrow.
    if (isRecipient) return 'accepted';
    return 'awaiting_funding';
  }
  if (status === 'rejected') return 'rejected';
  if (status === 'withdrawn') return 'withdrawn';
  return status;
}

export const IS_TYPES_MODULE = true;
